import React from "react";
import {
  GridCallbackDetails,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import { PagedResponse } from "../types/Result";

export default function useDataGridPaging<T>(page: PagedResponse<T>) {
  const location = useLocation();
  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>({
      page: page.currentPage, // Convert to 0-based for DataGrid
      pageSize: page.pageSize,
    });

  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [],
  });

  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    page.sort ? [{ field: page.sort.field, sort: page.sort.sort }] : []
  );

  const navigate = useNavigate();

  const getPathFromFilterModel = (model: GridFilterModel) => {
    let path = "";
    if (model.items && model.items.length > 0) {
      model.items
        .filter((d) => d.value !== "")
        .forEach((item, index) => {
          if (item.field && item.operator && item.value !== undefined) {
            path += `&filters[${index}].field=${encodeURIComponent(
              item.field
            )}`;
            path += `&filters[${index}].operator=${encodeURIComponent(
              item.operator
            )}`;
            path += `&filters[${index}].value=${encodeURIComponent(
              item.value.toString()
            )}`;
          }
        });
    }
    return path;
  };

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
      let path = `${location.pathname}?pageNumber=${model.page + 1}&pageSize=${
        model.pageSize
      }`;
      if (sortModel.length > 0 && sortModel[0]) {
        path += `&sort.sort=${sortModel[0].sort}&sort.field=${sortModel[0].field}`;
      }
      path += getPathFromFilterModel(filterModel);
      navigate(path);
    },
    [location.pathname, sortModel, filterModel, navigate]
  );

  const handleSortModelChange = React.useCallback(
    (model: GridSortModel, details: GridCallbackDetails) => {
      setSortModel(model);

      const sort = model[0];
      let path = `${location.pathname}?pageNumber=${
        paginationModel.page + 1
      }&pageSize=${paginationModel.pageSize}`;
      if (sort) {
        path += `&sort.sort=${sort.sort}&sort.field=${sort.field}`;
      }
      path += getPathFromFilterModel(filterModel);
      navigate(path);
    },
    [location.pathname, paginationModel, filterModel, navigate]
  );

  const handleFilterChange = React.useCallback(
    (model: GridFilterModel, details: GridCallbackDetails<"filter">) => {
      if (!details.reason) return;
      if (!["deleteFilterItem", "upsertFilterItem"].includes(details.reason))
        return;

      setFilterModel(model);

      let path = `${location.pathname}?pageNumber=${
        paginationModel.page + 1
      }&pageSize=${paginationModel.pageSize}`;
      if (sortModel.length > 0 && sortModel[0]) {
        path += `&sort.sort=${sortModel[0].sort}&sort.field=${sortModel[0].field}`;
      }
      path += getPathFromFilterModel(model);
      navigate(path);
    },
    [location.pathname, paginationModel, sortModel, navigate]
  );

  return {
    paginationModel,
    filterModel,
    sortModel,
    handlePaginationModelChange,
    handleSortModelChange,
    handleFilterChange,
  };
}
